/**
 * Markdown 本文中の「添付ファイルリンク」に関するユーティリティ。
 *
 * 共有ノートの添付は DB のファイルテーブルを持たず、本文 Markdown に
 * `[📎 元のファイル名](S3 公開 URL)` として埋め込まれる（NoteEditor が挿入）。
 * S3 キーはアップロード時に `{name}_{yymmddHHMMSS}_{uuid8}{ext}` へ一意化される
 * （BE `_generate_unique_filename`）ため、保存ファイル名の復元にはラベル
 * （📎 の後ろ = アップロード時の元ファイル名）を最優先し、ラベルが無い
 * 旧データは S3 キーからサフィックスを除去して復元する。
 */

import { mediaKindOfUrl } from "./markdownMedia";

/** NoteEditor が添付リンクのラベル先頭に付ける目印。 */
export const ATTACHMENT_LABEL_PREFIX = "📎";

/**
 * purpose=note でアップロードされた S3 ファイル URL か（キーが notes/ 配下）。
 * 外部サイトのファイルは fetch が CORS で失敗することが多いため、
 * 添付カード化・zip 同梱の対象にしない。
 *
 * アプリ内のディープリンク（`/notes/{noteId}` 等。deepLink.ts 参照）を
 * 添付と誤認しないよう、同一オリジンの URL は対象外にする（添付の実体は
 * S3 / CDN 上にあり、アプリと同じオリジンには存在しない）。さらに S3 キーが
 * `notes/{user_id}/{ファイル名}` 形式（BE upload.py）であることを利用し、
 * `notes` セグメントの後ろに 2 セグメント以上続く URL だけを添付とみなす
 * （path-style の `/{bucket}/notes/…` も許容する）。
 */
export const isNoteUploadUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    if (parsed.origin === window.location.origin) return false;
    const segments = parsed.pathname.split("/").filter(Boolean);
    const notesIndex = segments.indexOf("notes");
    if (notesIndex === -1) return false;
    const segmentsAfterNotes = segments.length - notesIndex - 1;
    return segmentsAfterNotes >= 2;
  } catch {
    return false;
  }
};

const decodeSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

/**
 * purpose=task_board でアップロードされた S3 ファイル URL なら、その S3 キー
 * （`{project_id}/task_board/{ファイル名}`。BE `PURPOSE_PREFIX_MAP`）を返す。
 * virtual-hosted / path-style どちらの URL でも、`task_board` の 1 つ手前の
 * セグメントからをキーとみなす。同一オリジンを除外する理由は isNoteUploadUrl と同じ。
 */
export const taskBoardUploadKeyOfUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    if (parsed.origin === window.location.origin) return null;
    const segments = parsed.pathname.split("/").filter(Boolean);
    const index = segments.indexOf("task_board");
    if (index < 1 || segments.length - index - 1 < 1) return null;
    return segments.slice(index - 1).map(decodeSegment).join("/");
  } catch {
    return null;
  }
};

/** purpose=task_board でアップロードされた S3 ファイル URL か */
export const isTaskBoardUploadUrl = (url: string): boolean =>
  taskBoardUploadKeyOfUrl(url) !== null;

/** ノート・タスクボードの本文へ直接埋め込まれたアップロードファイルの URL か */
export const isEmbeddedUploadUrl = (href: string | undefined): href is string =>
  !!href && (isNoteUploadUrl(href) || isTaskBoardUploadUrl(href));

/**
 * S3 アップロード時に一意化のため付与されるサフィックス
 * （`{name}_{yymmddHHMMSS}_{uuid8}{ext}`、末尾に `_original` が続く形も含む）
 * を除去して元のファイル名へ戻す。BE 側の `clean_s3_filename` と同じ規則。
 * サフィックスが無ければそのまま返す。
 */
export const stripUploadSuffix = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  const stem = dot > 0 ? fileName.slice(0, dot) : fileName;
  const ext = dot > 0 ? fileName.slice(dot) : "";
  const cleaned = stem.replace(/_\d{12}_[a-f0-9]{8}(?:_original)?$/, "");
  return (cleaned || stem) + ext;
};

/**
 * URL からファイル名（デコード済み basename）を取り出し、S3 の一意化
 * サフィックスと OS のファイル名に使えない文字を除去して返す。
 */
export const fileNameFromUrl = (url: string): string => {
  const clean = url.split(/[?#]/)[0];
  const raw = clean.split("/").pop() ?? "";
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    /* 不正なパーセントエンコードはそのまま使う */
  }
  const cleaned = stripUploadSuffix(decoded)
    // eslint-disable-next-line no-control-regex
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, "-")
    .trim();
  return cleaned || "file";
};

/**
 * ノート本文中のリンクを「添付ファイルカード」として表示すべきか。
 * purpose=note でアップロードされた S3 ファイルのうち、インライン表示を持たない
 * 非メディア（PDF / zip 等）だけを対象にする（画像・音声・動画は従来表示のまま）。
 */
export const isAttachmentLink = (href: string | undefined): href is string => {
  if (!href) return false;
  return isNoteUploadUrl(href) && mediaKindOfUrl(href) === null;
};

/**
 * 添付リンクの保存ファイル名を解決する。
 * ラベルが `📎 名前` 形式ならその名前（= アップロード時の元ファイル名）、
 * それ以外（旧データ・編集済みラベル）は URL からサフィックスを除去して復元する。
 */
export const attachmentFileName = (label: string, href: string): string => {
  const trimmed = label.trim();
  if (trimmed.startsWith(ATTACHMENT_LABEL_PREFIX)) {
    const name = trimmed.slice(ATTACHMENT_LABEL_PREFIX.length).trim();
    if (name) return name;
  }
  return fileNameFromUrl(href);
};
